import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

export interface UserLocation {
  city: string;
  state: string;
  lat: number;
  lng: number;
  radius: number;
}

const STORAGE_KEY = 'user_location';
const DEFAULT_RADIUS = 25;
const NOMINATIM_BASE = 'https://nominatim.openstreetmap.org';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private readonly isBrowser: boolean;
  private readonly locationSubject = new BehaviorSubject<UserLocation | null>(null);

  readonly location$ = this.locationSubject.asObservable();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private readonly http: HttpClient,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);

    if (this.isBrowser) {
      this.loadFromStorage();
    }
  }

  get snapshot(): UserLocation | null {
    return this.locationSubject.getValue();
  }

  /**
   * Attempt to detect location via the browser Geolocation API.
   * On success, reverse-geocodes the coordinates to a city/state and saves.
   * Returns the resolved location or null if permission was denied or geocoding failed.
   */
  async detectFromBrowser(): Promise<UserLocation | null> {
    if (!this.isBrowser || !navigator.geolocation) {
      return null;
    }

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 10000,
          maximumAge: 300000,
        });
      });

      const { latitude, longitude } = position.coords;
      const reversed = await this.reverseGeocode(latitude, longitude);

      if (!reversed) {
        return null;
      }

      const location: UserLocation = {
        city: reversed.city,
        state: reversed.state,
        lat: latitude,
        lng: longitude,
        radius: this.snapshot?.radius ?? DEFAULT_RADIUS,
      };

      this.save(location);
      return location;
    } catch {
      return null;
    }
  }

  /**
   * Set location from a manually entered city and state.
   * Forward-geocodes to get lat/lng. Returns null if geocoding fails.
   */
  async setManualLocation(city: string, state: string): Promise<UserLocation | null> {
    if (!this.isBrowser) return null;

    const coords = await this.forwardGeocode(city, state);
    if (!coords) {
      return null;
    }

    const location: UserLocation = {
      city: city.trim(),
      state: state.trim().toUpperCase(),
      lat: coords.lat,
      lng: coords.lng,
      radius: this.snapshot?.radius ?? DEFAULT_RADIUS,
    };

    this.save(location);
    return location;
  }

  /**
   * Update the search radius while keeping the existing location.
   */
  setRadius(miles: number): void {
    const current = this.snapshot;
    if (!current) return;

    const updated: UserLocation = { ...current, radius: miles };
    this.save(updated);
  }

  /**
   * Clear the stored location.
   */
  clear(): void {
    if (this.isBrowser) {
      localStorage.removeItem(STORAGE_KEY);
    }
    this.locationSubject.next(null);
  }

  // ── Private helpers ─────────────────────────────────────────────────────────

  private loadFromStorage(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as UserLocation;
        if (typeof parsed.lat === 'number' && !isNaN(parsed.lat) && typeof parsed.lng === 'number' && !isNaN(parsed.lng) && parsed.city) {
          this.locationSubject.next(parsed);
        }
      }
    } catch {
      // Ignore malformed storage
    }
  }

  private save(location: UserLocation): void {
    if (this.isBrowser) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
    }
    this.locationSubject.next(location);
  }

  private async reverseGeocode(lat: number, lng: number): Promise<{ city: string; state: string } | null> {
    try {
      const url = `${NOMINATIM_BASE}/reverse?lat=${lat}&lon=${lng}&format=json`;
      const result = await firstValueFrom(
        this.http.get<any>(url, {
          headers: { 'Accept-Language': 'en' },
        })
      );

      if (!result?.address) return null;

      const addr = result.address;
      const city = addr.city || addr.town || addr.village || addr.county || '';
      const stateCode = addr['ISO3166-2-lvl4']
        ? addr['ISO3166-2-lvl4'].split('-')[1]
        : addr.state || '';

      if (!city || !stateCode) return null;

      return { city, state: stateCode };
    } catch {
      return null;
    }
  }

  private async forwardGeocode(city: string, state: string): Promise<{ lat: number; lng: number } | null> {
    try {
      const query = encodeURIComponent(`${city}, ${state}, USA`);
      const url = `${NOMINATIM_BASE}/search?q=${query}&format=json&limit=1&countrycodes=us`;
      const results = await firstValueFrom(
        this.http.get<any[]>(url, {
          headers: { 'Accept-Language': 'en' },
        })
      );

      if (!results || results.length === 0) return null;

      return {
        lat: parseFloat(results[0].lat),
        lng: parseFloat(results[0].lon),
      };
    } catch {
      return null;
    }
  }
}
